import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AppConfig } from 'AppConfig';
import { fetchData } from 'features/user/userActions';
import * as UserTypes from 'features/user/userTypes';
import { store } from 'store/store';

import { getAuthContext, getEnums, getTenantContext } from './api/client.js';
import { ACTIVATION_PATHS } from './navConfig.js';
import { ActivationEngineOverview } from './screens/ActivationEngineOverview.jsx';
import { EnrollmentDashboard } from './screens/EnrollmentDashboard.jsx';
import { OpportunitiesExplorer } from './screens/OpportunitiesExplorer.jsx';
import { OpportunityMonitoring } from './screens/OpportunityMonitoring.jsx';
import { Methodology } from './screens/Methodology.jsx';
import { HowToGuide } from './screens/HowToGuide.jsx';
import { ActivationWorkspace } from './screens/ActivationWorkspace.jsx';
import { CampaignBuilder } from './screens/CampaignBuilder.jsx';
import { CampaignDetail } from './screens/CampaignDetail.jsx';
import { RestrictedOpportunityReview } from './screens/RestrictedOpportunityReview.jsx';
import { Loading, ErrorCard } from './components/ui.jsx';
import { Shell } from './components/Shell.jsx';

// Activation Engine fonts + styles. The stylesheets are scoped to
// .activation-engine-host (see tokens.css / activation-engine-host.css) so they
// do not affect the rest of BenInsights. The host shim must load last.
import '@fontsource/noto-sans/latin-400.css';
import '@fontsource/noto-sans/latin-500.css';
import '@fontsource/noto-sans/latin-600.css';
import '@fontsource/noto-sans/latin-700.css';
import '@fontsource/rubik/latin-500.css';
import '@fontsource/rubik/latin-600.css';
import '@fontsource/rubik/latin-700.css';
import './styles/tokens.css';
import './styles/app.css';
import './styles/front-office.css';
import './styles/charts.css';
import './styles/activation-engine-host.css';

// The campaign detail screen needs the :campaignId path param; a tiny wrapper
// keeps the screen's existing prop contract (campaignId) unchanged.
function CampaignDetailRoute(props) {
  const { campaignId } = useParams();
  return <CampaignDetail {...props} campaignId={campaignId} />;
}

// Back-compat: redirect the pre-rehaul "/activation/*" URLs to "/campaigns/*".
function LegacyActivationRedirect() {
  const params = useParams();
  const { search } = useLocation();
  const rest = params['*'] ? `/${params['*']}` : '';
  return <Navigate to={`${ACTIVATION_PATHS.campaigns}${rest}${search}`} replace />;
}

export default function ActivationEngineNavigator() {
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const [tenant, setTenant] = useState(null);
  const [enums, setEnums] = useState(null);
  const [auth, setAuth] = useState(null);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!currentTenantCd) return;
    let cancelled = false;

    setTenant(null);
    setEnums(null);
    setAuth(null);
    setError(null);

    // An app switch that echoes the committed tenant: the tenant dimension defers to any
    // pending selection so this does not re-assert a tenant that is already changing.
    store.dispatch(fetchData(currentTenantCd, AppConfig.APP_CODE_ACTIVATION_ENGINE, { explicitTenant: false }))
      .then((contextApplied) => {
        if (cancelled) return null;
        if (!contextApplied) {
          throw new Error('Activate is not available for this tenant or user.');
        }
        store.dispatch({ type: UserTypes.APP_SWITCHED, payload: AppConfig.HEADER_MENU_TYPE.STANDARD });
        return Promise.all([getTenantContext(), getEnums(), getAuthContext()]);
      })
      .then((result) => {
        if (!result || cancelled) return;
        const [tenantData, enumData, authData] = result;
        setTenant(tenantData);
        setEnums(enumData);
        setAuth(authData);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });

    return () => {
      cancelled = true;
    };
  }, [currentTenantCd]);

  let content;
  if (error) {
    content = (
      <Shell title="Activate" activeNav="overview">
        <ErrorCard error={error} />
        <p style={{ color: 'var(--bi-muted)' }}>
          The application backend is unavailable. Please try again shortly, or contact support if the problem
          persists.
        </p>
      </Shell>
    );
  } else if (!tenant || !enums || !auth) {
    content = (
      <Shell title="Activate" activeNav="overview">
        <Loading />
      </Shell>
    );
  } else {
    const shared = { tenant, enums, auth };
    content = (
      <Routes>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<ActivationEngineOverview {...shared} />} />
        <Route path="enrollment" element={<EnrollmentDashboard {...shared} />} />
        <Route path="opportunities" element={<OpportunitiesExplorer {...shared} params={searchParams} />} />
        <Route path="secure-review" element={<RestrictedOpportunityReview {...shared} />} />
        <Route path="monitoring" element={<OpportunityMonitoring {...shared} params={searchParams} />} />
        <Route path="methodology" element={<Methodology {...shared} params={searchParams} />} />
        <Route path="how-to" element={<HowToGuide {...shared} />} />
        <Route path="campaigns/new" element={<CampaignBuilder {...shared} params={searchParams} />} />
        <Route path="campaigns/action" element={<CampaignBuilder {...shared} params={searchParams} />} />
        <Route
          path="campaigns/:campaignId"
          element={<CampaignDetailRoute {...shared} params={searchParams} />}
        />
        <Route path="campaigns" element={<ActivationWorkspace {...shared} />} />
        {/* Back-compat: pre-rehaul /activation/* URLs redirect to /campaigns/*. */}
        <Route path="activation/*" element={<LegacyActivationRedirect />} />
        <Route path="*" element={<Navigate to="overview" replace />} />
      </Routes>
    );
  }

  return <div className="activation-engine-host">{content}</div>;
}
